import { http, pagination, id } from 'utils'
import { set, concat, merge, findIndex, get } from 'lodash'
import { SET_GRADUATES, SET_META, ADD_GRADUATES, SET_GRADUATE, RESET_GRADUATE, ADD_DOCUMENT, SET_GRADUATE_PLACEMENT, SET_PROPERTY } from 'vx/mutations'
import capabilities from './graduates/capabilities'

const modules = {
  capabilities
}

export const initialGraduateState = () => ({
  first_name: null,
  last_name: null,
  username: null,
  email: null,
  role: null,
  agency_id: null,
  region: null,
  phone: null,
  unenrolled: null,
  gen: null,
  enabled: true,
  initial_capabilities_set: false,
  documents: [],
  assessment: null
})

// Graduates state.
const state = {
  graduate: initialGraduateState(),
  graduates: [],
  meta: null
}

// Graduates state getters.
const getters = {
  graduateExists: (state) => state.graduate && state.graduate.id !== null && state.graduate.id !== undefined,
  strengthText: state => get(state, 'graduate.is_starting_2021', false)
    ? 'Strength'
    : 'Likely to be a strength'
}

// Graduates actions
const actions = {
  /**
   * Get all graduates.
   *
   * @return Promise
   */
  async getGraduates ({ commit }, params = {}) {
    commit(SET_GRADUATES, [])
    commit(SET_META, null)

    const response = await http.get('graduates', { params })
    commit(SET_GRADUATES, response.data.data)
    commit(SET_META, response.data.meta)

    return response
  },

  /**
   * Get next lot of graduates
   *
   * not being used at the moment, but keeping it in case they decide to add
   * pagination in the future
   *
   * @param {*} params
   */
  async getGraduatesForNextPage ({ commit, state }, params = {}) {
    if (pagination(state.meta)) {
      return Promise.reject(new Error('No more results.'))
    }

    params.page = state.meta.current_page + 1

    const response = await http.get('graduates', { params })

    commit(ADD_GRADUATES, response.data.data)
    commit(SET_META, response.data.meta)
  },

  /**
   * Get a graduate by id
   *
   * @param integer id
   */
  async getGraduate ({ commit }, id) {
    commit(RESET_GRADUATE)

    const response = await http.get(`graduates/${id}`)

    commit(SET_GRADUATE, response.data.data)

    return response
  },

  /**
   * Delete a graduate's document
   */
  deleteDocument ({ commit }, { graduate, document }) {
    return http.delete(`/graduates/${id(graduate)}/documents/${id(document)}`)
  },

  /**
   * Store a graduate's performance assessment
   */
  async storePerformanceAssessment ({ commit }, { graduate, payload }) {
    const response = await http.post(`/graduates/${id(graduate)}/assessments`, payload)

    return response
  },

  /**
   * Update a graduate's performance assessment
   */
  async updatePerformanceAssessment ({ commit }, { graduate, assessment, payload }) {
    const response = await http.put(`/graduates/${id(graduate)}/assessments/${id(assessment)}`, payload)

    return response
  },

  /**
   * Reset graduate to initial state
   */
  resetGraduate ({ commit }) {
    commit(RESET_GRADUATE)
  },

  addDocument ({ commit }, document) {
    commit(ADD_DOCUMENT, document)
  },

  setGraduatePlacement ({ commit }, placement) {
    commit(SET_GRADUATE_PLACEMENT, placement)
  },

  setGraduateProperty ({ commit }, payload) {
    commit(SET_PROPERTY, payload)
  },

  resetGraduateAssessment ({ commit }) {
    commit(SET_PROPERTY, { property: 'assessment', value: null, force: true })
  }
}

// Graduates state mutations.
const mutations = {
  [SET_GRADUATES] (state, graduates) {
    state.graduates = graduates
  },

  [SET_META] (state, meta) {
    state.meta = meta
  },

  [ADD_GRADUATES] (state, graduates) {
    state.graduates = concat(state.graduates, graduates)
  },

  [SET_GRADUATE] (state, graduate) {
    state.graduate = merge(initialGraduateState(), graduate)
  },

  [RESET_GRADUATE] (state, graduate) {
    state.graduate = initialGraduateState()
  },

  [ADD_DOCUMENT] (state, document) {
    state.graduate.documents.push(document)
  },

  [SET_GRADUATE_PLACEMENT] (state, placement) {
    let index = findIndex(state.graduate.placements, existing => existing.id === placement.id)

    if (index !== -1) {
      state.graduate.placements.splice(index, 1, placement)
    }
  },

  [SET_PROPERTY] (state, { property, value, force = false }) {
    if (force && typeof value === 'object') {
      // Force state reactivity for graduate state
      const copiedGraduate = Object.assign({}, state.graduate)
      set(copiedGraduate, property, value)
      state.graduate = copiedGraduate
    } else {
      set(state.graduate, property, value)
    }
  }
}

// Export our state, getters, actions, and mutations.
export default {
  namespaced: true, state, getters, actions, mutations, modules
}
